import React, { useEffect, useMemo, useState } from 'react'
import { MultiSelect } from '@progress/kendo-react-dropdowns'
import { filterBy } from '@progress/kendo-data-query'
import { getReportUserScope, ReportUserOption, ReportUserScope } from '../../data/reportUserScopeData'

/**
 * The user filter shared by the pipeline and quote/notes reports.
 *
 * Starts on all users (an empty selection) for somebody with the All users
 * option, and on just themselves otherwise. The choices are themselves plus
 * their direct reports, or everybody for an All users user. The API checks the
 * same rules, so this is about offering the right choices, not enforcing them.
 */
export const useReportUserFilter = (enabled: boolean = true) => {
  const [scope, setScope] = useState<ReportUserScope | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [loaded, setLoaded] = useState(false)

  const defaultIds = (s: ReportUserScope | null) =>
    !s || s.canViewAllUsers ? [] : [s.currentEmployeeId]

  useEffect(() => {
    if (!enabled) return

    getReportUserScope()
      .then((result) => {
        setScope(result)
        setSelectedIds(defaultIds(result))
      })
      .catch((error) => {
        // Left unloaded, the reports still run on the API's default for this
        // user - all users or just themselves - so nothing is shown wrongly.
        console.error('Error loading report user filter:', error)
      })
      .finally(() => setLoaded(true))
  }, [enabled])

  // What an empty selection means, spelled out: every user for an All users
  // user, otherwise just themselves. For endpoints that do not apply that
  // default themselves.
  const effectiveIds = useMemo(
    () => (selectedIds.length > 0 ? selectedIds : defaultIds(scope)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, scope]
  )

  return {
    scope,
    selectedIds,
    effectiveIds,
    setSelectedIds,
    /** False until the default selection is in place, so a first fetch can wait for it. */
    ready: loaded,
    /** Back to all users, or just the current user, as the filter started. */
    reset: () => setSelectedIds(defaultIds(scope)),
    /** The picked users' names, for export file names and headings. */
    selectedNames: (scope?.employees ?? [])
      .filter((e) => selectedIds.includes(e.employeeId))
      .map((e) => e.employeeName),
  }
}

interface ReportUserFilterProps {
  scope: ReportUserScope | null
  value: number[]
  onChange: (employeeIds: number[]) => void
  disabled?: boolean
  label?: string
  labelClassName?: string
  labelStyle?: React.CSSProperties
  width?: string
}

const ReportUserFilter = ({
  scope,
  value,
  onChange,
  disabled,
  label = 'Users',
  labelClassName,
  labelStyle,
  width = '260px',
}: ReportUserFilterProps) => {
  // Kendo hands filtering back to us when `filterable` is set, so the typed
  // text is held here and the list narrowed below.
  const [search, setSearch] = useState('')

  const options = scope?.employees ?? []
  const choices = search
    ? filterBy(options, { field: 'employeeName', operator: 'contains', ignoreCase: true, value: search })
    : options

  // Emptying the filter means everybody for an All users user, and the API's
  // default of just themselves for anyone else - the placeholder says which.
  const placeholder = scope?.canViewAllUsers ? 'All users' : 'Just me'

  return (
    <div>
      <label className={labelClassName} style={labelStyle}>{label}</label>
      <MultiSelect
        data={choices}
        textField="employeeName"
        dataItemKey="employeeId"
        value={options.filter((o: ReportUserOption) => value.includes(o.employeeId))}
        onChange={(e) => {
          onChange((e.value as ReportUserOption[]).map((o) => o.employeeId))
          setSearch('')
        }}
        filterable
        onFilterChange={(e) => setSearch(e.filter.value)}
        placeholder={placeholder}
        autoClose={false}
        // Nobody else to pick: a user with no direct reports only ever sees
        // themselves, so there is nothing to choose.
        disabled={disabled || !scope || (!scope.canViewAllUsers && options.length <= 1)}
        style={{ width }}
      />
    </div>
  )
}

export default ReportUserFilter
